/* -------------------------------------------------------
   useConversations  —  conversation-list management
   Fetches summaries, handles rename / soft-delete,
   tracks the currently-active conversation.
   ------------------------------------------------------- */

import { useState, useRef, useCallback, useEffect } from 'react';
import {
  listConversations,
  getConversation,
  renameConversation,
  deleteConversation,
} from '../api/chatV2';
import type { ConversationSummary, ConversationDetail } from '../types/chatV2';

export function useConversations() {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /* ---- fetch full list ---- */
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listConversations();
      if (!mountedRef.current) return;
      // Sort: most recent first
      list.sort(
        (a, b) =>
          new Date(b.lastMessageAt).getTime() -
          new Date(a.lastMessageAt).getTime(),
      );
      setConversations(list);
    } catch (err) {
      // Silently fail — sidebar just stays empty
      console.error('Failed to load conversations', err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    refresh();
  }, [refresh]);

  /* ---- load a single conversation's full messages ---- */
  const load = useCallback(
    async (id: string): Promise<ConversationDetail | null> => {
      try {
        const detail = await getConversation(id);
        setActiveId(id);
        return detail;
      } catch (err) {
        console.error('Failed to load conversation', err);
        return null;
      }
    },
    [],
  );

  /* ---- rename ---- */
  const rename = useCallback(async (id: string, title: string) => {
    try {
      await renameConversation(id, title);
      if (!mountedRef.current) return;
      setConversations((prev) =>
        prev.map((c) => (c.id === id ? { ...c, title } : c)),
      );
    } catch (err) {
      console.error('Failed to rename conversation', err);
      throw err; // let UI handle
    }
  }, []);

  /* ---- delete ---- */
  const remove = useCallback(
    async (id: string) => {
      try {
        await deleteConversation(id);
        if (!mountedRef.current) return;
        setConversations((prev) => prev.filter((c) => c.id !== id));
        if (activeId === id) setActiveId(null);
      } catch (err) {
        console.error('Failed to delete conversation', err);
        throw err;
      }
    },
    [activeId],
  );

  return {
    conversations,
    isLoading,
    activeId,
    setActiveId,
    refresh,
    load,
    rename,
    remove,
  };
}
